import { useState, useEffect, useRef } from "react";
import "./PlayerStatsModal.sass";

/**
 * PlayerStatsModal - Modal que muestra las estadísticas detalladas de un jugador
 * 
 * Props:
 *   - player: Objeto del jugador con datos y estadísticas
 *   - isOpen: Boolean que indica si el modal debe estar abierto
 *   - onClose: Función callback para cerrar el modal
 *   - side: String que indica si es 'home' o 'away' para estilos
 */
export default function PlayerStatsModal({ player, isOpen, onClose, side = "home" }) {
  const modalRef = useRef(null);
  const [modalStyle, setModalStyle] = useState({});

  // Cerrar modal al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Cerrar modal al presionar ESC
    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscKey);

      // Ajustar posición del modal para que no salga de pantalla
      if (modalRef.current) {
        setTimeout(() => {
          const modal = modalRef.current;
          const modalRect = modal.getBoundingClientRect();

          let newStyle = {};

          // Si sobresale a la derecha
          if (modalRect.right > window.innerWidth - 10) {
            newStyle.right = "0";
            newStyle.left = "auto";
          }
          // Si sobresale a la izquierda
          else if (modalRect.left < 10) {
            newStyle.left = "0";
            newStyle.right = "auto";
          }

          setModalStyle(newStyle);
        }, 0);
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !player) {
    return null;
  }

  // Extraer datos del jugador
  const {
    name,
    firstName,
    lastName,
    shirt,
    positionLabel,
    captain,
    stats = [],
  } = player;

  // Ordenar estadísticas de forma lógica
  const statsOrder = [
    "touches",
    "passes",
    "pass_accuracy",
    "shots",
    "shots_on_target",
    "tackles",
    "fouls_committed",
    "fouls_won",
    "chances_created",
  ];

  const sortedStats = stats.sort((a, b) => {
    const indexA = statsOrder.indexOf(a.key);
    const indexB = statsOrder.indexOf(b.key);
    return (indexA === -1 ? statsOrder.length : indexA) - (indexB === -1 ? statsOrder.length : indexB);
  });

  return (
    <div className={`player-stats-modal-overlay player-stats-modal-overlay--${side}`}>
      <div
        ref={modalRef}
        className={`player-stats-modal player-stats-modal--${side}`}
        style={modalStyle}
      >
        {/* Header del modal */}
        <div className="player-stats-modal-header">
          <button
            className="player-stats-modal-close"
            onClick={onClose}
            aria-label="Cerrar modal"
          >
            ✕
          </button>

          <div className="player-stats-header-content">
            {/* Número de camiseta */}
            <div className={`player-stats-shirt ${side === "home" ? "home" : "away"}`}>
              {shirt}
            </div>

            {/* Información del jugador */}
            <div className="player-stats-header-info">
              <h2 className="player-stats-name">
                {String(name).toUpperCase()}
                {captain && <span className="player-stats-captain" title="Capitán">C</span>}
              </h2>
              <p className="player-stats-position">{positionLabel}</p>
            </div>
          </div>
        </div>

        {/* Contenido de estadísticas */}
        <div className="player-stats-content">
          {sortedStats && sortedStats.length > 0 ? (
            <div className="player-stats-list">
              {sortedStats.map((stat, index) => (
                <div key={index} className="player-stats-item">
                  <span className="player-stats-label">{stat.label}</span>
                  <span className="player-stats-value">{stat.value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="player-stats-empty">No hay estadísticas disponibles</p>
          )}
        </div>
      </div>
    </div>
  );
}
